import { gql, TypedDocumentNode } from "@apollo/client";

import {
  GetRuleExecutionResultAssociatedCasesQuery,
  GetRuleExecutionResultAssociatedCasesQueryVariables,
} from "src/graphql/anti-fraud/__generated__/graphql";
import { propertyValueFragment } from "src/graphql/anti-fraud/fragments/PropertyValueFragment";
import { useFetchQuery } from "src/graphql/utils";
import { mapToPropertyValue } from "src/mappers/property.mappers";

export const getRuleExecutionResultAssociatedCasesQuery = gql`
  query GetRuleExecutionResultAssociatedCases(
    $ruleExecutionResultId: ID!
    $offset: Int!
    $limit: Int!
    $searchString: String
  ) {
    ruleExecutionResultAssociatedCases(
      ruleExecutionResultId: $ruleExecutionResultId
      options: { searchString: $searchString, pagination: { offset: $offset, limit: $limit } }
    ) {
      cases {
        id
        typeCode
        properties {
          ...PropertyValueFragment
        }
      }
      pageInfo {
        totalCount
        limit
        offset
      }
    }
  }

  ${propertyValueFragment}
` as TypedDocumentNode<
  GetRuleExecutionResultAssociatedCasesQuery,
  GetRuleExecutionResultAssociatedCasesQueryVariables
>;

export type AssociatedCasesResult = {
  cases: {
    id: string;
    typeCode: string;
    properties: ReturnType<typeof mapToPropertyValue>[];
  }[];
  pageInfo: {
    totalCount: number;
    limit: number;
    offset: number;
  };
};

export const useGetRuleExecutionResultAssociatedCases = (options: {
  ruleExecutionResultId: string | undefined;
  offset: number;
  limit: number;
  searchString?: string;
}) => {
  return useFetchQuery(getRuleExecutionResultAssociatedCasesQuery, {
    toData: (data): AssociatedCasesResult => {
      if (!data?.ruleExecutionResultAssociatedCases) {
        return {
          cases: [],
          pageInfo: {
            totalCount: 0,
            limit: options.limit,
            offset: options.offset,
          },
        };
      }

      return {
        cases: data.ruleExecutionResultAssociatedCases.cases.map((c) => ({
          id: c.id,
          typeCode: c.typeCode,
          properties: c.properties.map(mapToPropertyValue),
        })),
        pageInfo: {
          totalCount: data.ruleExecutionResultAssociatedCases.pageInfo.totalCount,
          limit: data.ruleExecutionResultAssociatedCases.pageInfo.limit,
          offset: data.ruleExecutionResultAssociatedCases.pageInfo.offset,
        },
      };
    },
    isEmpty: (data) => !data || !data?.ruleExecutionResultAssociatedCases,
    queryOptions: {
      variables: {
        ruleExecutionResultId: options.ruleExecutionResultId ?? "",
        offset: options.offset,
        limit: options.limit,
        searchString:
          options.searchString && options.searchString.length > 0 ? options.searchString : null,
      },
      skip: !options.ruleExecutionResultId,
    },
    uiErrors: {
      fetchError: "FETCH-RELATED-CASES-ERROR",
    },
  });
};
